import type { SupportedLocale } from '@/i18n'
import type { BrandDocMeta } from '@/types/brand-doc-meta'
import { RUN_TIMED_OUT_ERROR_CODE, type SetupRun } from '@/types/setup-run'

export const COMPANY_SIZE_BANDS = ['solo', '2-10', '11-50', '51-200', '201-1000', '1000+'] as const

export type CompanySizeBand = (typeof COMPANY_SIZE_BANDS)[number]

/** The two line rows of the profile, in page order. */
export const PROFILE_ROW_IDS = ['tried', 'blockers'] as const

export type ProfileRowId = (typeof PROFILE_ROW_IDS)[number]

export const PROFILE_BASES = ['drafted', 'edited', 'empty'] as const

type ProfileBasis = (typeof PROFILE_BASES)[number]

export interface ProfileIdentity {
  readonly title: string
  readonly companySize: CompanySizeBand | null
  readonly basis: ProfileBasis
}

/** One job story: the moment, the progress wanted then, and what it gives. */
export interface JobStory {
  readonly when: string
  readonly want: string
  readonly soThat: string
}

export interface ProfileJob {
  readonly ladder: { readonly goal: string; readonly progress: string }
  readonly stories: readonly JobStory[]
  readonly basis: ProfileBasis
}

export interface ProfileRow {
  readonly items: readonly string[]
  readonly basis: ProfileBasis
}

export interface ProfileSummary {
  readonly text: string
  readonly basis: ProfileBasis
}

export interface CustomerProfile {
  readonly meta: BrandDocMeta
  readonly identity: ProfileIdentity
  readonly job: ProfileJob
  readonly tried: ProfileRow
  readonly blockers: ProfileRow
  readonly summary: ProfileSummary
  readonly language: SupportedLocale
  readonly runAt: string | null
}

export const CUSTOMER_PROFILE_RUN_STAGES = ['draft'] as const

export type CustomerProfileRunStage = (typeof CUSTOMER_PROFILE_RUN_STAGES)[number]

export const CUSTOMER_PROFILE_ERROR_CODES = [
  'customer_profile_enqueue_failed',
  'customer_profile_failed',
  'customer_profile_facts_missing',
  RUN_TIMED_OUT_ERROR_CODE,
] as const

export type CustomerProfileErrorCode = (typeof CUSTOMER_PROFILE_ERROR_CODES)[number]

export const CUSTOMER_PROFILE_RUN_STALE_AFTER_MS = 5 * 60 * 1000

/** One customer-profile draft: one stage and no entries. */
export interface CustomerProfileRun extends SetupRun<CustomerProfileRunStage> {
  readonly errorCode?: CustomerProfileErrorCode
  readonly keepEdited: boolean
  readonly startedByUserId: string
}

export type CustomerProfileUpdate =
  | {
      readonly section: 'row'
      readonly card: ProfileRowId
      readonly items: readonly string[]
    }
  | {
      readonly section: 'identity'
      readonly title: string
      readonly companySize: CompanySizeBand | null
      readonly summaryText: string
    }

export const PROFILE_CHIP_LABEL_MAX_LENGTH = 80

export const PROFILE_TITLE_MAX_LENGTH = 120

export const PROFILE_SUMMARY_MAX_LENGTH = 800

export const PROFILE_TRIED_MAX = 4

export const CUSTOMER_PROFILE_REFUSAL_REASONS = [
  'customer_profile_label_too_long',
  'customer_profile_label_required',
  'customer_profile_tried_too_many',
  'customer_profile_title_too_long',
  'customer_profile_summary_too_long',
  'customer_profile_rows_empty',
  'customer_profile_run_in_progress',
  'customer_profile_facts_missing',
  'customer_profile_competitors_missing',
  'owner_admin_role_required',
  'editor_role_required',
] as const

export type CustomerProfileRefusalReason = (typeof CUSTOMER_PROFILE_REFUSAL_REASONS)[number]

export type ProfileIdentityInput = Omit<
  Extract<CustomerProfileUpdate, { section: 'identity' }>,
  'section'
>

export function isCustomerProfileApproved(profile: CustomerProfile): boolean {
  return profile.meta.status === 'approved'
}
